import React, { useState, useRef, useMemo, useEffect } from "react";
import { cn } from "@/lib/utils";
import { Textarea } from "@/components/ui/textarea";
import { apiPost } from "@/utils/apiHelper";
import { UPLOAD_API } from "@/config/api";
import {
  FileStatus,
  PresignUploadRequest,
  PresignUploadResponse,
} from "@/types";
import { toast } from "@/hooks/useToast";
import { useChatStore } from "@/stores/chatStore";
import { generateId } from "@/lib/utils";
import { IconButton, Loading } from "@/components/fortress";
import { Plus, Vector } from "@/components/icons";
import { throwCasaApiError } from "@/utils";

interface InputBarProps {
  onSendMessage: (message: string, isFromOption?: boolean) => void;
  isLoading: boolean;
  onMessageChange?: (message: string, isEmpty: boolean) => void;
  // onEndChat: () => void;
}

const MAX_FILE_COUNT = 3;

export const InputBar: React.FC<InputBarProps> = ({
  onSendMessage,
  isLoading,
  onMessageChange,
  // onEndChat,
}) => {
  const { addFile, files, updateFile } = useChatStore();
  const [message, setMessage] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isEmpty = useMemo(() => {
    return message.trim().length === 0 && files.length === 0;
  }, [message, files]);

  // 通知父组件 message 状态变化
  useEffect(() => {
    onMessageChange?.(message, isEmpty);
  }, [message, isEmpty, onMessageChange]);

  const canBeSent = useMemo(() => {
    const hasFilesInUploading = files.some(
      (file) => file.status === FileStatus.UPLOADING
    );

    return !isEmpty && !isLoading && !hasFilesInUploading;
  }, [isEmpty, isLoading, files]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (canBeSent) {
      onSendMessage(message);
      setMessage("");
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.nativeEvent.isComposing && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // 自动调整 Textarea 高度
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // 重置高度以获取正确的 scrollHeight
    textarea.style.height = "auto";

    // 计算行高
    const lineHeight = parseFloat(getComputedStyle(textarea).lineHeight);
    const minHeight = lineHeight; // 1 行
    const maxHeight = lineHeight * 5; // 5 行

    // 设置高度
    const scrollHeight = textarea.scrollHeight;
    if (scrollHeight < minHeight) {
      textarea.style.height = `${minHeight}px`;
    } else if (scrollHeight > maxHeight) {
      textarea.style.height = `${maxHeight}px`;
      textarea.style.overflowY = "auto";
    } else {
      textarea.style.height = `${scrollHeight}px`;
      textarea.style.overflowY = "hidden";
    }
  }, [message]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    if (files.length > MAX_FILE_COUNT) {
      toast({
        description: "File count cannot exceed 3",
      });
      return;
    }

    for (const file of files) {
      if (file.size > 5 * 1024 * 1024) {
        toast({
          description: "File size cannot exceed 5MB",
        });
        return;
      }
    }

    setIsUploading(true);

    try {
      // 并行上传所有文件
      const uploadPromises = Array.from(files).map(async (file) => {
        const id = generateId();

        try {
          // 添加文件到状态
          addFile({
            id,
            localUrl: URL.createObjectURL(file),
            url: "",
            name: file.name,
            size: file.size,
            type: file.type,
            status: FileStatus.UPLOADING,
          });

          // 步骤1: 获取预签名URL
          const presignRequest: PresignUploadRequest = {
            file_name: file.name,
          };

          const presignResponse = await apiPost<PresignUploadResponse>(
            UPLOAD_API.presign(),
            presignRequest
          );

          const { upload_url, file_url } = presignResponse.data;

          // 步骤2: 使用预签名URL直接上传到云存储
          const uploadResponse = await fetch(upload_url, {
            method: "PUT",
            body: file,
          });

          if (!uploadResponse.ok) {
            throw new Error(
              `Upload failed with status: ${uploadResponse.status}`
            );
          }

          // 步骤3: 发送文件信息到聊天
          updateFile({
            id,
            url: file_url,
            name: file.name,
            size: file.size,
            type: file.type,
            status: FileStatus.SUCCESSED,
          });

          return { success: true, file: file.name };
        } catch (error) {
          console.error("File upload failed:", error);
          const errorMessage =
            error instanceof Error ? error.message : "Please try again later";

          updateFile({
            id,
            url: "",
            name: file.name,
            size: file.size,
            type: file.type,
            status: FileStatus.FAILED,
          });

          toast({
            description: `File ${file.name} upload failed: ${errorMessage}`,
          });

          return { success: false, file: file.name, error: errorMessage };
        }
      });

      // 等待所有上传完成
      await Promise.all(uploadPromises);
    } catch (error) {
      console.error("Batch upload failed:", error);
      throwCasaApiError(error, "[InputBar] Batch upload failed");
    } finally {
      // 只在所有文件处理完成后重置状态
      setIsUploading(false);

      // 清空 input 值
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex-1 flex items-center"
    >
      {/* 输入框 */}
      <div className="flex-1 flex items-end relative pl-[7px] pr-[11px] border rounded-[24px] border-[#E0DDD2]">
        {/* 文件上传按钮 */}
        <button
          type="button"
          className={cn("shrink-0 flex items-center justify-center my-[7px] w-8 h-8 text-primary bg-[#F6F3E7] rounded-full", {
            "cursor-not-allowed opacity-50":
              isLoading || isUploading || files.length >= MAX_FILE_COUNT,
          })}
          onClick={() => fileInputRef.current?.click()}
          disabled={isLoading || isUploading || files.length >= MAX_FILE_COUNT}
        >
          {isUploading ? <Loading theme="dark" /> : <Plus />}
        </button>

        <input
          ref={fileInputRef}
          className="hidden"
          type="file"
          onChange={handleFileUpload}
          multiple={true}
          // 仅允许 png, jpg, jpeg, gif, webp
          accept="image/png, image/jpeg, image/gif, image/webp"
        />

        <div className="flex-1 self-stretch flex items-center py-[7px]">
          <Textarea
            ref={textareaRef}
            className="py-0 px-4 h-auto border-none leading-[1.2] resize-none overflow-hidden"
            placeholder="Ask Casa"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyPress}
            disabled={isLoading}
            rows={1}
          />
        </div>

        <button
          className="shrink-0 flex items-center justify-center my-[7px] w-8 h-8 text-primary disabled:opacity-50"
          type="submit"
          disabled={isLoading}
        >
          {isLoading ? <Loading theme="dark" /> : <Vector />}
        </button>
      </div>
    </form>
  );
};
